import { Box, Button, Flex } from '@chakra-ui/react';
import { ReactComponent as LeftArrowIcon } from 'assets/icons/carousel_arrow_left.svg';
import { ReactComponent as RightArrowIcon } from 'assets/icons/carousel_arrow_right.svg';
import { loadFonts } from 'hooks/useFont/useFontLoader';
import { PageThumbnail } from 'modules/Editor/components/PageManager/Slides/Slide';
import { setActivePage, useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectInfographSize, selectPageOrder } from 'modules/Editor/store/infographSelector';
import { findFontsInPage } from 'modules/Editor/store/infographSlice.helpers';
import { setFontsLoaded } from 'modules/Editor/store/pageControlSlice';
import { useEffect, useRef, useState } from 'react';
import { InfographState } from 'types/infographTypes';

type DesignPreviewProps = {
  infographState: InfographState;
};

const SCROLL_OFFSET = 200;

/**
 * Render the carousel preview and controller for multi page infographics
 * @param {infographState} infographState
 * @returns
 */
export const DesignPreviewCarousel = ({ infographState }: DesignPreviewProps) => {
  const dispatch = useAppDispatch();
  const pageOrder = useAppSelector(selectPageOrder);
  const infographSize = useAppSelector(selectInfographSize);
  const ref = useRef<HTMLDivElement>(null);
  const [enableLeftScrollButton, setEnableLeftScrollButton] = useState(true);
  const [enableRightScrollButton, setEnableRightScrollButton] = useState(false);

  // Update the main preview when thumbnail is clicked
  const handleClick = (pageId: string) => {
    dispatch(setActivePage({ pageId }));
  };

  const scroll = (scrollOffset: number) => {
    if (ref?.current?.scrollLeft !== undefined) {
      const newOffset = ref.current.scrollLeft + scrollOffset;
      ref.current.scrollLeft += scrollOffset;
      setEnableLeftScrollButton(newOffset > 0);
      setEnableRightScrollButton(newOffset < ref?.current?.scrollWidth - ref?.current?.clientWidth);
    }
  };

  useEffect(() => {
    for (const pageId of pageOrder) {
      // Initialize fonts for all pages
      loadFonts(findFontsInPage(infographState, pageId)).finally(() => {
        dispatch(setFontsLoaded(true));
      });
      scroll(0);
    }
  }, [infographState, pageOrder, dispatch]);

  return pageOrder.length > 1 ? (
    <Flex alignItems='center'>
      <Box
        display='flex'
        flexDir='row'
        sx={{
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
        overflow='auto'
        overflowY='hidden'
        scrollBehavior='smooth'
        ref={ref}
      >
        {pageOrder.map((pageId) => (
          <Box key={pageId} onClick={() => handleClick(pageId)} cursor='pointer'>
            <PageThumbnail isActivePage={false} infographSize={infographSize} isDragging={false} pageId={pageId} />
          </Box>
        ))}
      </Box>

      {/* Scroll Control */}
      <Flex position='absolute' justifyContent='space-between' width='full' pointerEvents='none'>
        <Button
          visibility={enableLeftScrollButton ? 'visible' : 'hidden'}
          pointerEvents={enableLeftScrollButton ? 'auto' : 'none'}
          left='-7'
          width='14'
          height='14'
          borderRadius='full'
          bg='white'
          shadow='md'
          onClick={() => scroll(-SCROLL_OFFSET)}
        >
          <LeftArrowIcon />
        </Button>
        <Button
          visibility={enableRightScrollButton ? 'visible' : 'hidden'}
          pointerEvents={enableRightScrollButton ? 'auto' : 'none'}
          left='7'
          width='14'
          height='14'
          borderRadius='full'
          bg='white'
          shadow='md'
          onClick={() => scroll(SCROLL_OFFSET)}
        >
          <RightArrowIcon />
        </Button>
      </Flex>
    </Flex>
  ) : null;
};
